import React, { useEffect, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { doc, getDoc, setDoc, Timestamp, collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useParams } from "react-router-dom";
import { useFirestoreCollection } from '../librairies/Firestore';
import Loader from '../components/Loader';
import PrimaryButton from '../components/buttons/PrimaryButton';
import { convertSecondsToTime, datetimeFromTimestampToString, timeFromTimestampToString } from '../librairies/Dates';
import ConfirmModal from '../components/modals/ConfirmModal';
import { useNavigate } from 'react-router-dom';

const STATES = [
  { label: "État neuf", color: "bg-green-500", value: "Neuf" },
  { label: "Bon état", color: "bg-cyan-500", value: "Bon état" },
  { label: "Moyen", color: "bg-yellow-500", value: "Moyen" },
  { label: "Usagé", color: "bg-red-500 text-white", value: "Usagé" },
  { label: "Contrôle non effectué", color: "bg-gray-500 text-white", value: "Non effectué" },
  { label: "Inexistant", color: "bg-black text-white", value: "Inexistant" },
];

const ModeMaintenance = () => {

  //contexte de l'utilisateur connecté
  const user = useUser();
  const navigate = useNavigate();
  
  const { client_id, site_id, silo_id, machine_id } = useParams();
  const [machine, setMachine] = useState(null);
  const [error, setError] = useState(null); // Gérer les erreurs.
  const [showValidModal, setShowValidModal] = useState(false); // Afficher le modal de confirmation
  const [isSubmitting, setIsSubmitting] = useState(false); // État de chargement lors de la soumission
  const [formData, setFormData] = useState({
    etats: {}, // Pour stocker les réponses radio
    annotation: "", // Pour stocker les annotations textuelles
    files: [], // Pour stocker les fichiers
    filePreviews: [], // Pour stocker les URLs d'aperçu
  });
  const [dateDebutMaintenance, setDateDebutMaintenance] = useState(Timestamp.now());
  const { data: composants, loader } = useFirestoreCollection(`clients/${client_id}/sites/${site_id}/silos/${silo_id}/machines/${machine_id}/composants`);


  useEffect(() => {
    getMachine();
  }, []); 


  //on récupère les infos de la machine
  const getMachine = async () => {
    const documentRef = doc(db, `clients/${client_id}/sites/${site_id}/silos/${silo_id}/machines/${machine_id}`);
    
    // Récupérer le document depuis Firestore
    const docSnap = await getDoc(documentRef);

    // Vérifiez si le document existe
    if (docSnap.exists()) {
      const data = docSnap.data();
      setMachine(data);
    } else {
      console.log("Aucun document trouvé !");
    }
  }

  const handleStateChange = (composantId, composantNom, etat) => {
    setFormData((prev) => ({
      ...prev,
      etats: {
        ...prev.etats,
        [composantId]: { nom: composantNom, etat: etat },
      },
    }));
  };

  const handleTextareaChange = (text) => {
    setFormData((prev) => ({ ...prev, annotation: text }));
  };

  const handleMultipleFiles = (files) => {
    const fileArray = Array.from(files); // Convertir FileList en tableau
    const updatedFiles = [...formData.files, ...fileArray]; // Ajouter les nouveaux fichiers
    const previews = updatedFiles.map((file) => URL.createObjectURL(file)); // Générer les URLs pour l'aperçu
  
    setFormData((prev) => ({
      ...prev, // Conserver les autres champs de formData
      files: updatedFiles,
      filePreviews: previews,
    }));
  };

  const removeImage = (index) => {
    setFormData((prev) => {
      const updatedFiles = prev.files.filter((_, i) => i !== index); // Supprime le fichier à cet index
      const updatedPreviews = prev.filePreviews.filter((_, i) => i !== index); // Supprime l'aperçu à cet index
  
      return {
        ...prev,
        files: updatedFiles,
        filePreviews: updatedPreviews,
      };
    });
  };


  const handleSubmit = async () => {

    if(Object.entries(formData.etats).length < composants.length){
      setError("Veuillez sélectionner un état pour chaque composant.");
      //on cache la popup de validation
      setShowValidModal(false);
      return;
    }

    // Empêcher les doubles soumissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {

      // Étape 1 : Upload des photos dans Firebase Storage
      // Upload des images avec des noms uniques
     
      const uploadPromises = formData.files.map(async (file, index) => {
        const uniqueFileName = `${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}.${file.name.split('.').pop()}`;
        const fileRef = ref(
          storage,
          `${client_id}/${site_id}/${silo_id}/${machine_id}/images/${uniqueFileName}`
        );
        await uploadBytes(fileRef, file); // Upload du fichier
        const downloadURL = await getDownloadURL(fileRef); // Obtenir l'URL
        return downloadURL; // Retourner l'URL
      });

      // Récupérer les URLs après upload
      const photoURLs = await Promise.all(uploadPromises);

      // Étape 2 : Préparation des données du rapport
      const reportData = {
        machine: doc(db, `clients/${client_id}/sites/${site_id}/silos/${silo_id}/machines/${machine_id}`),
        etats: formData.etats, // Les réponses radio pour chaque composant
        annotation: formData.annotation || '', // Les annotations textuelles
        dateDebutMaintenance: dateDebutMaintenance, // Date de début du rapport
        dateFinMaintenance: Timestamp.now(), // Date de fin du rapport
        dateDebutMaintenanceString: datetimeFromTimestampToString(dateDebutMaintenance.seconds), // Date de début du rapport
        dateFinMaintenanceString: datetimeFromTimestampToString(Timestamp.now().seconds), // Date de fin du rapport
        duree: Timestamp.now().seconds - dateDebutMaintenance.seconds, // Durée de la maintenance en secondes
        dureeString: convertSecondsToTime(Timestamp.now().seconds - dateDebutMaintenance.seconds),
        title: 'Rapport maintenance '+machine.nom,
        photos: photoURLs.map(url => ({ url })), // URLs des photos après upload,
        _pdfplum_config: {
          outputFileName:"rapport_machine_"+Timestamp.now().seconds+".pdf",
          outputStoragePrefix: `${client_id}/${site_id}/${silo_id}/${machine_id}`
        }
      };
  
      // Étape 3 : Référence pour le document dans Firestore
      const userReportsCollection = collection(
        db,
        `utilisateurs/${user.user.user.uid}/create_rapports_maintenance`
      );
  
      // Créer un nouveau document pour le rapport
      const reportRef = await addDoc(userReportsCollection, reportData);

      //on redirige vers la page liste des machines
      navigate(`/client/${client_id}/site/${site_id}/silo/${silo_id}`); 
    } catch (error) {
      console.error('Erreur lors de la soumission du formulaire :', error);
      alert('Une erreur est survenue lors de la soumission du formulaire.');
      setIsSubmitting(false); // Réactiver le bouton en cas d'erreur
    } finally {
      setIsSubmitting(false); // S'assurer que l'état est réinitialisé même en cas de succès
    }
  };



  return (
    <div className='pt-4 md:min-h-screen justify-center items-center bg-gray-100'>
      <p className='font-bold text-lg text-center'>Mode maintenance {machine? machine.nom : ""}</p>
      <div className='mt-6'>
        {loader ? (
          <Loader />  
        ) : (
          (composants && composants.length > 0) ? (
            <div className='px-2 md:px-6'>
              <div className='my-4'>
                {composants.map((composant, index) => {
                  return(
                    <div className='' key={index}>
                      <p className='text-lg font-semibold pb-1'>{composant.nom}</p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {STATES.map((state) => (
                          <label
                            key={state.value}
                            className={`flex items-center gap-2 cursor-pointer ${state.color} px-4 py-2 rounded`}
                          >
                            <input
                              type="radio"
                              name={`state-${composant.id}`}
                              value={state.value}
                              onChange={() => handleStateChange(composant.id, composant.nom, state.value)}
                            />
                            {state.label}
                          </label>
                        ))}
                      </div>
                      <hr className="h-px my-1 bg-gray-200 border-0 dark:bg-gray-700" />
                    </div>
                  )
                })}
              </div>
              <div className='my-4'>
                <p className='font-bold'>Annotations</p>
                {/* Global Textarea */}
                <textarea
                  placeholder="Ajouter des informations générales..."
                  className="w-full p-2 border rounded mb-4"
                  onChange={(e) => handleTextareaChange(e.target.value)}

                ></textarea>
              </div>
              {/* File Upload */}
              <div className='my-4'>
                <p className='font-bold'>Photos</p>
                <input
                  type="file"
                  accept="image/*"
                  multiple // Permet de sélectionner plusieurs images
                  className="mb-4"
                  onChange={(e) => handleMultipleFiles(e.target.files)}
                />
              </div>
              {/* Preview of Uploaded Photos */}
              {formData.filePreviews && formData.filePreviews.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {formData.filePreviews.map((src, index) => (
                    <div key={index} className="relative">
                      {/* Image Preview */}
                      <img
                        src={src}
                        alt={`Preview ${index + 1}`}
                        className="w-24 h-24 object-cover rounded shadow"
                      />
                      {/* Delete Icon */}
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600 transition"
                        aria-label="Supprimer l'image"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {error && <p className="text-red-500">{error}</p>}

              {/* Submit Button */}
              <a href="#" onClick={() => !isSubmitting && setShowValidModal(true)} className="flex justify-center pb-8">
                <PrimaryButton type={"button"} value={"Enregistrer"} loading={isSubmitting} disabled={isSubmitting} />
              </a>
            </div>
          ) : (
            <p className='text-center'>Aucun composant pour cette machine</p>
          )
        )}
      </div>
      {showValidModal ? (
        <ConfirmModal 
          closeAction={() => !isSubmitting && setShowValidModal(false)} 
          confirmAction={() => handleSubmit()} 
          isSubmitting={isSubmitting}
        />
      ) : null }
    </div>
  );
};

export default ModeMaintenance;
